// API Security — Events page.
//
// Dedicated unified feed for every security signal across the API
// security platform: auth failures (Phase 1), spec violations
// (Phase 2), GraphQL control failures (Phase 5), and any
// blocked observation (Phase 0). Backed by the new CP route
// /asec/:site_uid/events which UNION-ALL's the 4 underlying CH
// streams and normalises them into a single envelope.
//
// Same visual primitives as Posture/Anomaly — `WAFEvent`-style
// table head + body, mono-blue treatment for host + path,
// drawer drill-in for full row context.

import React from "react";
import { useLocation } from "react-router-dom";
import $ from "jquery";
import {
  Stack, Box, Typography, Select, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow,
  Skeleton, Chip,
  Tooltip,
} from "@mui/material";
import ReactCountryFlag from "react-country-flag";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import KeyIcon from "@mui/icons-material/KeyOutlined";
import RuleIcon from "@mui/icons-material/Rule";
import AccountTreeIcon from "@mui/icons-material/AccountTreeOutlined";
import BlockIcon from "@mui/icons-material/BlockOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";

import asec from "../../../../../utils/axios/v1/asecAxios";
import AsecEventDrawer from "./AsecEventDrawer";
import colors from "../../../../../utils/colors";
import { formatDate } from "../../../../../utils/format";
import AsecPage from "./AsecPage";
import { EventsTimeRangePicker } from "../../../../common/Greens";
import { useNotification } from "../../../../../contexts/NotificationContext";
import { getPeriodString } from "../../../application/analytics/common";
import { tableBodySx } from "../../detection/WAFEvent";
import { getDefaultEventListingCustomDateRange } from "../../detection/eventTimeRange";
import {
  AsecEventsTableShell,
  AsecEventsHeadCell,
  AsecEventsBodyText,
  AsecEventsViewCell,
  AsecEventsTableFooter,
  asecEventRowSx,
} from "./components/AsecEventsTable";
import { AsecEventsFilterBar } from "./components/AsecEventsFilterBar";
import WAFOverviewMetricCard, {
  WAFOverviewIconChip,
  WAFOverviewStatNumber,
  wafOverviewGridSx,
  wafOverviewSubSx,
} from "../web_application/components/WAFOverviewMetricCard";
import EventsActivityHeatmap from "../../detection/WAFEventsHeatmap";
import RefreshIconButton from "../../../../common/RefreshIconButton";
import {
  filterSelectSx,
  asecOverviewMetricCardSx,
} from "./asecUi";

const INDIGO = colors.primary[300];
const INDIGO_INK = colors.primary[500];
const INDIGO_SOFT = "rgba(99,113,247,0.10)";
const INDIGO_BORDER = "rgba(99,113,247,0.30)";
const ROWS_PER_PAGE = 25;

/** Resolve preset or custom time range to ISO bounds for asec event APIs. */
function getAsecTimeBounds(timeRange, customDateRange) {
  if (timeRange === "custom" && customDateRange?.length === 2) {
    return {
      from: new Date(customDateRange[0]).toISOString(),
      to: new Date(customDateRange[1]).toISOString(),
    };
  }
  const to = new Date();
  const from = new Date(to);
  switch (timeRange) {
    case "30m":
      from.setMinutes(from.getMinutes() - 30);
      break;
    case "60m":
      from.setMinutes(from.getMinutes() - 60);
      break;
    case "3h":
      from.setHours(from.getHours() - 3);
      break;
    case "6h":
      from.setHours(from.getHours() - 6);
      break;
    case "24h":
      from.setHours(from.getHours() - 24);
      break;
    case "3d":
      from.setDate(from.getDate() - 3);
      break;
    case "7d":
      from.setDate(from.getDate() - 7);
      break;
    default:
      from.setHours(from.getHours() - 24);
  }
  return { from: from.toISOString(), to: to.toISOString() };
}

const CATEGORY_META = {
  auth: {
    label: "Auth",
    Icon: KeyIcon,
    color: "#7a5b10",
    bg: "rgba(255,219,67,0.18)",
    border: "rgba(223,180,0,0.40)",
  },
  spec_validation: {
    label: "Spec",
    Icon: RuleIcon,
    color: INDIGO_INK,
    bg: INDIGO_SOFT,
    border: INDIGO_BORDER,
  },
  closed_world: {
    // Path-not-declared rejections — distinct from shape failures.
    // Slate tone keeps it visually separated from indigo "Spec".
    label: "Closed-world",
    Icon: LockOutlinedIcon,
    color: "#475569",
    bg: "rgba(71,85,105,0.10)",
    border: "rgba(71,85,105,0.30)",
  },
  graphql: {
    label: "GraphQL",
    Icon: AccountTreeIcon,
    color: colors.green[300],
    bg: "rgba(31,193,107,0.10)",
    border: "rgba(31,193,107,0.30)",
  },
  blocked: {
    label: "Blocked",
    Icon: BlockIcon,
    color: colors.red[200],
    bg: "rgba(251,55,72,0.10)",
    border: "rgba(251,55,72,0.30)",
  },
  dlp: {
    // PII redactions from the api-observations dlp_redactions counter
    // (Layerd Phase-4 scope scanning). Violet to separate it from
    // the red "Blocked" tone — redaction is not a block.
    label: "DLP",
    Icon: PrivacyTipOutlinedIcon,
    color: "#7c3aed",
    bg: "rgba(124,58,237,0.10)",
    border: "rgba(124,58,237,0.30)",
  },
};

// ─── Helpers ────────────────────────────────────────────────────────
const nfmt = (n) => (typeof n === "number" && isFinite(n) ? n.toLocaleString() : "—");
const ACTION_RESULTS = ["invalid", "unmatched", "response_invalid"];

const CATEGORY_SEVERITY = {
  blocked: { label: "Critical", color: "#B91C1C", bgColor: "#FEE2E2" },
  closed_world: { label: "High", color: "#C2410C", bgColor: "#FFEDD5" },
  dlp: { label: "Medium", color: "#B45309", bgColor: "#FEF3C7" },
  spec_validation: { label: "Medium", color: "#B45309", bgColor: "#FEF3C7" },
  graphql: { label: "Low", color: "#15803D", bgColor: "#DCFCE7" },
  auth: { label: "Low", color: "#15803D", bgColor: "#DCFCE7" },
};

function getAsecDetectionDisplay(ev) {
  const meta = CATEGORY_META[ev?.category];
  const title = meta?.label || ev?.category || "—";
  const subtitleParts = [];
  if (ev?.failure_kind) subtitleParts.push(ev.failure_kind);
  if (ev?.failure_field) subtitleParts.push(ev.failure_field);
  const extraSources = Array.isArray(ev?.sources)
    ? ev.sources.filter((s) => s && s !== ev.category)
    : [];
  if (extraSources.length) {
    subtitleParts.push(
      `Also: ${extraSources.map((s) => CATEGORY_META[s]?.label || s).join(", ")}`
    );
  }
  return { title, subtitle: subtitleParts.length ? subtitleParts.join(" · ") : null };
}

function getAsecSeverityConfig(ev) {
  return CATEGORY_SEVERITY[ev?.category] || {
    label: "—",
    color: colors.neutral[700],
    bgColor: colors.neutral[100],
  };
}

function getAsecEventStatus(ev) {
  const result = (ev?.result || "").toLowerCase();
  if (result === "log_only") return "Log-only";
  if (
    result === "blocked"
    || result === "redacted"
    || ACTION_RESULTS.includes(result)
    || ev?.category === "blocked"
  ) {
    return "Blocked";
  }
  return "Observed";
}

function getAsecStatusColor(status) {
  switch (status) {
    case "Blocked":
      return colors.red[100];
    case "Log-only":
      return colors.yellow[100];
    case "Observed":
      return colors.green[300];
    default:
      return colors.neutral[800];
  }
}

function truncatePath(path, max = 36) {
  if (!path) return "—";
  return path.length > max ? `${path.slice(0, max)} ...` : path;
}
// ─── Skeleton rows ──────────────────────────────────────────────────
function SkeletonRows({ rows }) {
  const columnCount = 8;
  return (
    <TableBody>
      {Array.from({ length: rows }).map((_, r) => (
        <TableRow key={r}>
          {Array.from({ length: columnCount }).map((__, c) => (
            <TableCell key={c} sx={{ padding: "16px 8px" }}>
              <Skeleton variant="rectangular" height={22} sx={{ borderRadius: "11px" }} />
            </TableCell>
          ))}
          <TableCell sx={{ padding: "16px 8px", width: 48 }}>
            <Skeleton variant="circular" width={18} height={18} />
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  );
}

// ─── Hero metric cards — match WAF Event overview strip ─────────────
function HeroStrip({ stats, loading, timeRange, customDateRange }) {
  const periodLabel = getPeriodString(timeRange, customDateRange).toLowerCase();
  const total = stats?.total ?? null;
  const by = stats?.by_category || {};
  const skeletonStat = <Skeleton variant="rounded" width={72} height={32} sx={{ mt: 1.25, borderRadius: "6px" }} />;

  return (
    <Box sx={{
      ...wafOverviewGridSx,
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(2, 1fr)",
        md: "repeat(3, 1fr)",
        lg: "1.2fr repeat(6, minmax(0, 1fr))",
      },
    }}>
      <WAFOverviewMetricCard
        highlight
        icon={(
          <WAFOverviewIconChip variant="primary">
            <ShieldOutlinedIcon sx={{ fontSize: 16 }} />
          </WAFOverviewIconChip>
        )}
        label={`Total events · ${periodLabel}`}
        sx={{ ...asecOverviewMetricCardSx, gridColumn: { xs: "1", sm: "1 / -1", md: "1 / -1", lg: "auto" } }}
      >
        {loading ? skeletonStat : (
          <>
            <WAFOverviewStatNumber value={nfmt(total)} unit="events" />
            <Typography sx={wafOverviewSubSx}>Across all categories</Typography>
          </>
        )}
      </WAFOverviewMetricCard>

      {Object.entries(CATEGORY_META).map(([id, meta]) => {
        const { Icon } = meta;
        const count = by[id] || 0;
        const pctLabel = total > 0 ? `${((count / total) * 100).toFixed(1)}% of total` : null;
        return (
          <WAFOverviewMetricCard
            key={id}
            icon={(
              <WAFOverviewIconChip variant="primary">
                <Icon sx={{ fontSize: 16 }} />
              </WAFOverviewIconChip>
            )}
            label={meta.label}
            sx={asecOverviewMetricCardSx}
          >
            {loading ? skeletonStat : (
              <>
                <WAFOverviewStatNumber value={nfmt(count)} />
                <Typography sx={wafOverviewSubSx}>
                  {pctLabel ? (
                    <Box component="span" sx={{ fontWeight: 600, color: meta.color }}>{pctLabel}</Box>
                  ) : "—"}
                </Typography>
              </>
            )}
          </WAFOverviewMetricCard>
        );
      })}
    </Box>
  );
}

// ─── Body ───────────────────────────────────────────────────────────
function Body({ siteId }) {
  const { showError } = useNotification();
  // Deep-link params (set by AsecEventDrawer → Similar Events).
  // Read once on mount; we don't currently push back to the URL.
  const location = useLocation();
  const deepLinkParams = React.useMemo(() => {
    const q = new URLSearchParams(location.search || "");
    const want = [
      "host", "method", "path", "src_ip",
      "failure_kind", "error_kind", "block_reason",
      "identity_id", "identity_kind",
    ];
    const out = {};
    want.forEach((k) => { const v = q.get(k); if (v) out[k] = v; });
    return out;
  }, [location.search]);

  const [stats, setStats] = React.useState(null);
  const [statsLoading, setStatsLoading] = React.useState(true);
  const [timeRange, setTimeRange] = React.useState("7d");
  const [customDateRange, setCustomDateRange] = React.useState(() => getDefaultEventListingCustomDateRange());
  const [category, setCategory] = React.useState(() => {
    try {
      const q = new URLSearchParams(window.location.search || "");
      const v = q.get("category");
      if (v && CATEGORY_META[v]) return v;
    } catch (_) {}
    return "ALL";
  }); // ALL or one of CATEGORY_META keys
  const [methodFilter, setMethodFilter] = React.useState("ALL");
  // Outcome filter — default "all" (unfiltered). Honour ?outcome= when present.
  const [outcomeFilter, setOutcomeFilter] = React.useState(() => {
    try {
      const q = new URLSearchParams(window.location.search || "");
      const v = (q.get("outcome") || "").toLowerCase();
      if (v === "action" || v === "log_only" || v === "all") return v;
    } catch (_) {}
    return "all";
  });
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [items, setItems] = React.useState(null);
  // outcome semantics:
  //   action   = blocked=1 OR result is in {invalid, unmatched, response_invalid}
  //   log_only = result is "log_only" (would-have-been-blocked in enforce mode)
  //   all      = no outcome filter
  const matchOutcome = React.useCallback((row, mode) => {
    if (!row) return false;
    if (mode === "all") return true;
    const result = row.result || row._result || "";
    if (mode === "log_only") return result === "log_only";
    if (mode === "action") {
      if (Number(row.blocked) === 1) return true;
      return ACTION_RESULTS.includes(result);
    }
    return true;
  }, []);
  const filteredItems = React.useMemo(() => {
    if (!items) return null;
    const filtered = items.filter((row) => matchOutcome(row, outcomeFilter));
    if (outcomeFilter !== "all") return filtered;
    // For "All", pin action-taken rows to the top so the most operator-relevant
    // events lead. Stable sort: blocked first, then original order.
    return filtered.slice().sort((a, b) => {
      const aAction = matchOutcome(a, "action") ? 1 : 0;
      const bAction = matchOutcome(b, "action") ? 1 : 0;
      return bAction - aAction;
    });
  }, [items, outcomeFilter, matchOutcome]);
  const actionCount = React.useMemo(
    () => (items ? items.filter((r) => matchOutcome(r, "action")).length : 0),
    [items, matchOutcome]
  );
  const logOnlyCount = React.useMemo(
    () => (items ? items.filter((r) => matchOutcome(r, "log_only")).length : 0),
    [items, matchOutcome]
  );

  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [drawer, setDrawer] = React.useState({ open: false, ev: null });
  const [tick, setTick] = React.useState(0);

  const selectPeriod = (period) => {
    setTimeRange(period);
    if (period === "custom") {
      $(document).ready(() => {
        $(".react-minimal-datetime-range__range-input-wrapper").click();
        $(".react-minimal-datetime-range").css({
          "margin-top": $(".react-minimal-datetime-range__range-input-wrapper").parent().parent().parent().height() + 4,
        });
      });
    }
  };

  const selectCustomDateRange = (res) => {
    setCustomDateRange(res);
    setTimeRange("custom");
  };

  const timeBounds = React.useMemo(
    () => getAsecTimeBounds(timeRange, customDateRange),
    [timeRange, customDateRange]
  );

  const heatmapTimeRange = React.useMemo(
    () => ({ from: timeBounds.from, to: timeBounds.to }),
    [timeBounds]
  );

  const heatmapFilterKey = React.useMemo(
    () => JSON.stringify({ timeRange, customDateRange, category, methodFilter, search, outcomeFilter }),
    [timeRange, customDateRange, category, methodFilter, search, outcomeFilter]
  );

  const mapHeatmapRequest = React.useCallback((base) => {
    const params = {
      from: heatmapTimeRange.from,
      to: heatmapTimeRange.to,
      time_zone: base.time_zone,
      week_offset: base.week_offset,
      outcome: outcomeFilter,
    };
    if (category !== "ALL") params.categories = category;
    if (methodFilter !== "ALL") params.method = methodFilter;
    if (search) params.q = search;
    return params;
  }, [heatmapTimeRange, category, methodFilter, search, outcomeFilter]);

  const fetchHeatmap = React.useCallback(
    (params, opts) => asec.securityEventsHeatmap(siteId, params, opts),
    [siteId]
  );

  // Reset to page 0 whenever filters change so the visible window
  // stays meaningful.
  React.useEffect(() => { setPage(0); }, [timeRange, customDateRange, category, methodFilter, search, outcomeFilter]);

  React.useEffect(() => {
    if (!siteId) return;
    setStatsLoading(true);
    asec.securityEventsStats(siteId, { from: timeBounds.from, to: timeBounds.to })
      .then((r) => setStats(r.data))
      .catch((err) => {
        const msg = err?.response?.data?.message;
        if (msg) showError(msg);
        setStats(null);
      })
      .finally(() => setStatsLoading(false));
  }, [siteId, timeBounds, tick, showError]);

  React.useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    const params = {
      from: timeBounds.from,
      to: timeBounds.to,
      page,
      limit: ROWS_PER_PAGE,
    };
    if (category !== "ALL") params.categories = category;
    if (methodFilter !== "ALL") params.method = methodFilter;
    if (search) params.q = search;
    if (outcomeFilter && outcomeFilter !== "all") params.outcome = outcomeFilter;
    // Overlay any deep-link filters from the Similar Events buttons.
    Object.entries(deepLinkParams).forEach(([k, v]) => {
      if (v && params[k] === undefined) params[k] = v;
    });
    asec.securityEvents(siteId, params)
      .then((r) => {
        setItems(r.data?.items || []);
        setTotal(Number(r.data?.total || 0));
      })
      .catch((err) => {
        const msg = err?.response?.data?.message;
        if (msg) showError(msg);
        setItems([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [siteId, timeBounds, category, methodFilter, search, outcomeFilter, page, tick, deepLinkParams, showError]);

  const pageCount = Math.max(1, Math.ceil(total / ROWS_PER_PAGE));

  return (
    <Stack spacing={6}>
      <Stack spacing={2}>
        <AsecEventsFilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search path or operation…"
          trailing={(
            <>
              <RefreshIconButton
                onClick={() => setTick((n) => n + 1)}
                disabled={loading}
                loading={loading}
                size="compact"
              />
              <EventsTimeRangePicker
                selectPeriod={selectPeriod}
                selectCustomDateRange={selectCustomDateRange}
                timeRange={timeRange}
                customDateRange={customDateRange}
              />
            </>
          )}
        >
          <Select
            value={outcomeFilter}
            onChange={(e) => setOutcomeFilter(e.target.value)}
            sx={{ ...filterSelectSx, minWidth: 140 }}
          >
            <MenuItem value="all">All outcomes ({nfmt(stats?.total ?? 0)})</MenuItem>
            <MenuItem value="action">Action taken ({nfmt(actionCount)})</MenuItem>
            <MenuItem value="log_only">Log-only ({nfmt(logOnlyCount)})</MenuItem>
          </Select>
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            sx={{ ...filterSelectSx, minWidth: 140 }}
          >
            <MenuItem value="ALL">All categories ({nfmt(stats?.total ?? 0)})</MenuItem>
            {Object.entries(CATEGORY_META).map(([id, meta]) => (
              <MenuItem key={id} value={id}>
                {meta.label} ({nfmt(stats?.by_category?.[id] ?? 0)})
              </MenuItem>
            ))}
          </Select>
          <Select
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value)}
            sx={{ ...filterSelectSx, minWidth: 140 }}
          >
            <MenuItem value="ALL">All methods</MenuItem>
            {["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD"].map((m) => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </Select>
        </AsecEventsFilterBar>
      </Stack>

      <HeroStrip
        stats={stats}
        loading={statsLoading}
        timeRange={timeRange}
        customDateRange={customDateRange}
      />

      <EventsActivityHeatmap
        siteId={siteId}
        timeRange={heatmapTimeRange}
        requestFn={fetchHeatmap}
        mapRequestBody={mapHeatmapRequest}
        resetKey={heatmapFilterKey}
        exactSeries
      />

      <AsecEventsTableShell
        footer={(
          <AsecEventsTableFooter
            from={total > 0 ? page * ROWS_PER_PAGE + 1 : 0}
            to={Math.min(total, (page + 1) * ROWS_PER_PAGE)}
            total={total}
            label="events"
            page={page}
            pageCount={pageCount}
            onPageChange={setPage}
            summary={total > 0
              ? `${(page * ROWS_PER_PAGE + 1).toLocaleString()}–${Math.min(total, (page + 1) * ROWS_PER_PAGE).toLocaleString()} of ${nfmt(total)}`
              : "0 events"}
          />
        )}
      >
          <TableHead>
            <TableRow>
              <AsecEventsHeadCell width={280}>Event ID</AsecEventsHeadCell>
              <AsecEventsHeadCell>Hostname</AsecEventsHeadCell>
              <AsecEventsHeadCell>Activity</AsecEventsHeadCell>
              <AsecEventsHeadCell>IP address</AsecEventsHeadCell>
              <AsecEventsHeadCell>Type of detection</AsecEventsHeadCell>
              <AsecEventsHeadCell>Severity</AsecEventsHeadCell>
              <AsecEventsHeadCell>Status</AsecEventsHeadCell>
              <AsecEventsHeadCell width={230}>Timestamp</AsecEventsHeadCell>
              <AsecEventsHeadCell width={72} align="right">Action</AsecEventsHeadCell>
            </TableRow>
          </TableHead>
          {loading && (!items || items.length === 0)
            ? <SkeletonRows rows={ROWS_PER_PAGE} />
            : (items || []).length === 0 ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography textAlign="center" sx={tableBodySx}>
                      There are no events
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {filteredItems.map((ev, i) => {
                  const detection = getAsecDetectionDisplay(ev);
                  const severityConfig = getAsecSeverityConfig(ev);
                  const status = getAsecEventStatus(ev);
                  const eventId = ev.request_id;
                  const path = ev.path;
                  return (
                    <TableRow
                      key={ev.request_id || `${ev.ts}_${i}`}
                      onClick={() => setDrawer({ open: true, ev })}
                      sx={asecEventRowSx}
                    >
                      <TableCell>
                        <Typography
                          sx={{
                            ...tableBodySx,
                            fontFamily: "monospace",
                            color: "#1E40AF",
                            wordBreak: "break-all",
                          }}
                        >
                          {eventId || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <AsecEventsBodyText>{ev.host || "—"}</AsecEventsBodyText>
                      </TableCell>
                      <TableCell>
                        <Stack>
                          <AsecEventsBodyText>{ev.method || "—"}</AsecEventsBodyText>
                          {path?.length > 36 ? (
                            <Tooltip title={path} arrow>
                              <Typography sx={{ fontSize: 12, color: "#1E40AF", lineHeight: "14px" }}>
                                {truncatePath(path)}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography sx={{ fontSize: 12, color: "#1E40AF", lineHeight: "14px" }}>
                              {path || "—"}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          {ev.src_country ? (
                            <ReactCountryFlag
                              countryCode={ev.src_country}
                              svg
                              title={ev.src_country}
                              style={{ width: "26px", height: "18px" }}
                            />
                          ) : null}
                          <Typography sx={{ ...tableBodySx, pl: ev.src_country ? 2 : 0 }}>
                            {ev.src_ip || "—"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography sx={{ ...tableBodySx, fontWeight: 400, color: colors.neutral[1000] }}>
                            {detection.title}
                          </Typography>
                          {detection.subtitle ? (
                            <Typography
                              sx={{
                                fontSize: 12,
                                lineHeight: "18px",
                                fontWeight: 400,
                                color: colors.primary[300],
                              }}
                            >
                              {detection.subtitle}
                            </Typography>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {severityConfig.label && severityConfig.label !== "—" ? (
                        <Chip
                          size="small"
                          label={severityConfig.label}
                          sx={{
                            px: 1,
                            background: severityConfig.bgColor,
                            color: severityConfig.color,
                            borderRadius: "30px",
                            fontSize: 12,
                            fontWeight: "medium",
                          }}
                        />
                        ) : (
                          <Typography sx={tableBodySx}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              backgroundColor: getAsecStatusColor(status),
                              borderRadius: "100%",
                            }}
                          />
                          <Typography sx={tableBodySx}>{status}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography sx={tableBodySx}>{formatDate(ev.ts) || "—"}</Typography>
                      </TableCell>
                      <AsecEventsViewCell />
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
      </AsecEventsTableShell>

      <AsecEventDrawer
        open={drawer.open}
        event={drawer.ev}
        siteId={siteId}
        categoryMeta={CATEGORY_META}
        onClose={() => setDrawer({ open: false, ev: null })}
      />
    </Stack>
  );
}

export default function AsecEvents() {
  return (
    <AsecPage
      title="Events"
      helmetTitle="API Security — Events"
      pageDescription="Unified feed of auth failures, schema violations, GraphQL control failures, and blocked requests — one timeline for everything the API security platform observes."
      helpTooltip="Live feed of validation, auth, DLP, and other API-security events. Use the filters above the cards to narrow the feed. Click a row to see the full event payload."
    >
      {(siteId) => <Body siteId={siteId} />}
    </AsecPage>
  );
}
